const MAX_ENTRIES = 50;
const MAX_BYTES = 200 * 1024 * 1024; // 200MB

interface CacheEntry {
  chunks: Float32Array[];
  sampleRate: number;
  complete: boolean;
  merged: Float32Array | null;
  peaks: Float32Array | null;
  peakBarCount: number;
  totalSamples: number;
  byteSize: number;
  lastAccessTime: number;
}

export class AudioCache {
  private entries = new Map<string, CacheEntry>();
  private totalBytes = 0;

  static makeKey(messageId: string, voiceId: string): string {
    return `${messageId}:${voiceId}`;
  }

  append(key: string, samples: Float32Array, sampleRate: number) {
    const copy = samples.slice(0);
    const byteSize = copy.byteLength;

    let entry = this.entries.get(key);
    if (!entry) {
      entry = {
        chunks: [],
        sampleRate,
        complete: false,
        merged: null,
        peaks: null,
        peakBarCount: 0,
        totalSamples: 0,
        byteSize: 0,
        lastAccessTime: Date.now(),
      };
      this.entries.set(key, entry);
    }

    entry.chunks.push(copy);
    entry.totalSamples += copy.length;
    entry.byteSize += byteSize;
    entry.merged = null;
    entry.peaks = null;
    entry.lastAccessTime = Date.now();
    this.totalBytes += byteSize;

    this._evict();
  }

  markComplete(key: string) {
    const entry = this.entries.get(key);
    if (entry) {
      entry.complete = true;
    }
  }

  has(key: string): boolean {
    const entry = this.entries.get(key);
    return entry !== undefined && entry.complete;
  }

  hasPartial(key: string): boolean {
    return this.entries.has(key);
  }

  getMerged(key: string): { samples: Float32Array; sampleRate: number } | null {
    const entry = this.entries.get(key);
    if (!entry) return null;

    entry.lastAccessTime = Date.now();

    if (!entry.merged) {
      const merged = new Float32Array(entry.totalSamples);
      let offset = 0;
      for (const chunk of entry.chunks) {
        merged.set(chunk, offset);
        offset += chunk.length;
      }
      entry.merged = merged;
    }

    return { samples: entry.merged, sampleRate: entry.sampleRate };
  }

  getPeaks(key: string, barCount: number): Float32Array | null {
    barCount = Math.min(barCount, 500);
    const entry = this.entries.get(key);
    if (!entry || entry.totalSamples === 0) return null;

    entry.lastAccessTime = Date.now();

    if (entry.peaks && entry.peakBarCount === barCount) {
      return entry.peaks;
    }

    const merged = this.getMerged(key);
    if (!merged) return null;

    const { samples } = merged;
    const peaks = new Float32Array(barCount);
    const samplesPerBar = Math.floor(samples.length / barCount);

    for (let i = 0; i < barCount; i++) {
      const start = i * samplesPerBar;
      const end = Math.min(start + samplesPerBar, samples.length);
      let peak = 0;
      for (let j = start; j < end; j++) {
        const abs = Math.abs(samples[j]);
        if (abs > peak) peak = abs;
      }
      peaks[i] = peak;
    }

    entry.peaks = peaks;
    entry.peakBarCount = barCount;

    return peaks;
  }

  clear() {
    this.entries.clear();
    this.totalBytes = 0;
  }

  delete(key: string) {
    const entry = this.entries.get(key);
    if (entry) {
      this.totalBytes -= entry.byteSize;
      this.entries.delete(key);
    }
  }

  private _evict() {
    if (this.entries.size <= MAX_ENTRIES && this.totalBytes <= MAX_BYTES) return;

    // Only evict complete entries — incomplete ones are actively being written by the TTS worker
    const complete = [...this.entries.entries()]
      .filter(([, e]) => e.complete)
      .sort(([, a], [, b]) => a.lastAccessTime - b.lastAccessTime);

    for (const [key] of complete) {
      if (this.entries.size <= MAX_ENTRIES && this.totalBytes <= MAX_BYTES) break;
      this.delete(key);
    }
  }
}
